// Packages
import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import "moment-timezone";

// Components
import Header from "./../../../Components/Header/Header";
import ClockDetail from "./../../../Components/Modal/ClockDetail/ClockDetail";
import Text from "./../../../Components/FormControl/Text/Text";

// Services
import { getUserHistory, details } from "./../../../api/clock";
import { getTimesheetSignOff, signTimesheet } from "./../../../api/timesheetAttestation";

// Context
import { AppContext } from "./../../../Context/AppContext";
import { DEFAULT_TIMEZONE } from "Helpers/Constants";

// Images
import EditIcon from "./../../../Assets/images/edit.svg";

const TimeClockHistory = () => {
  const navigate = useNavigate();
  const [clockDetailsList, setClockDetailsList] = useState([]);
  const [clockDetails, setClockDetails] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [signOff, setSignOff] = useState([]);
  const [statement, setStatement] = useState("");
  const [signatureName, setSignatureName] = useState({});
  const { showToastMessage, setIsLoading, parentStoreTimezone } = useContext(
    AppContext
  );
  const timezone = parentStoreTimezone || DEFAULT_TIMEZONE;

  const handleGetClockDetailsList = async () => {
    setIsLoading(true);
    const response = await getUserHistory();

    if (response && response.status === 200)
      setClockDetailsList(response.data.data);
    else showToastMessage(undefined, response?.data?.message, "error", false);

    setIsLoading(false);
  };

  const handleGetSignOff = async () => {
    const response = await getTimesheetSignOff();

    if (response && response.status === 200) {
      setStatement(response.data.data.statement);
      setSignOff(response.data.data.weeks);
    } else showToastMessage(undefined, response?.data?.message, "error", false);
  };

  useEffect(() => {
    handleGetClockDetailsList();
    handleGetSignOff();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSign = async (event, week) => {
    event.preventDefault();

    setIsLoading(true);
    const response = await signTimesheet({
      period_start: week.period_start,
      signature_name: signatureName[week.period_start],
    });

    if (response && response.status === 200) await handleGetSignOff();
    else showToastMessage(undefined, response?.data?.message, "error", false);

    setIsLoading(false);
  };

  const renderSignOff = (week) => {
    if (!week) return null;

    if (week.signed_at)
      return (
        <div className="current-date text-right mb-5">
          Signed by {week.signature_name} on{" "}
          {moment.unix(week.signed_at).tz(timezone).format("ddd MMM DD, YYYY")}.
        </div>
      );

    if (!week.can_sign)
      return (
        <div className="current-date text-right mb-5">
          This work week has not ended yet.
        </div>
      );

    return (
      <div className="row align-items-center mb-5">
        <div className="col-md-6">{statement}</div>
        <div className="col-md-3">
          <Text
            type="text"
            label="Type your name"
            value={signatureName[week.period_start] || ""}
            onChange={(name) =>
              setSignatureName({ ...signatureName, [week.period_start]: name })
            }
          />
        </div>
        <div className="col-md-3 text-right">
          <a
            href="/"
            className="btn gray-btn d-inline-block"
            onClick={(event) => handleSign(event, week)}
          >
            Sign
          </a>
        </div>
      </div>
    );
  };

  const handleGetClockDetails = async (event, id) => {
    event.preventDefault();

    setIsLoading(true);
    const response = await details(id);

    if (response && response.status === 200) {
      setClockDetails(response.data.data);
      setShowModal(true);
    } else {
      showToastMessage(undefined, response.data.message, "error", false);
    }

    setIsLoading(false);
  };

  return (
    <React.Fragment>
      <Header title="Time History" />

      <div id="MainContent" className="main-content">
        {clockDetailsList.map((clockDetails, index) => {
          return (
            clockDetails.time_clock &&
            clockDetails.time_clock.length > 0 && (
              <div key={index}>
                <div className="current-date text-right">
                  {clockDetails.date}
                </div>
                <div className="table-responsive my-5">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th className="text-center">Hours</th>
                        <th className="text-center">OT (1.5)</th>
                        <th className="text-center">OT (2)</th>
                        <th className="text-center">Total Hours</th>
                        <th className="text-right" />
                      </tr>
                    </thead>
                    <tbody>
                      {clockDetails.time_clock.map((timeClock, index) => {
                        return (
                          <tr
                            key={index}
                            onClick={(event) =>
                              handleGetClockDetails(event, timeClock.id)
                            }
                            className="cursor-pointer"
                          >
                            <td>{timeClock.date}</td>
                            <td className="text-center">
                              {timeClock.regular_hours
                                ? timeClock.regular_hours
                                : "-"}
                            </td>
                            <td className="text-center">
                              {timeClock.ot1 ? timeClock.ot1 : "-"}
                            </td>
                            <td className="text-center">
                              {timeClock.ot2 ? timeClock.ot2 : "-"}
                            </td>
                            <td className="text-center">
                              {timeClock.duration ? timeClock.duration : "-"}
                            </td>
                            <td className="td-actions text-right">
                              <div>
                                <a
                                  className="link d-block"
                                  href="/"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    navigate(`/time-clock/${timeClock.id}`);
                                  }}
                                >
                                  <img src={EditIcon} alt="" />
                                </a>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      <tr className="total-row">
                        <td>Total Weekly Hours</td>
                        <td className="text-center">
                          {clockDetails.total_regular_hours
                            ? clockDetails.total_regular_hours
                            : "-"}
                        </td>
                        <td className="text-center">
                          {clockDetails.total_ot1
                            ? clockDetails.total_ot1
                            : "-"}
                        </td>
                        <td className="text-center">
                          {clockDetails.total_ot2
                            ? clockDetails.total_ot2
                            : "-"}
                        </td>
                        <td className="text-center">
                          {clockDetails.total_duration
                            ? clockDetails.total_duration
                            : "-"}
                        </td>
                        <td className="td-actions text-right" />
                      </tr>
                    </tbody>
                  </table>
                </div>

                {renderSignOff(
                  signOff.find((week) => week.date === clockDetails.date)
                )}
              </div>
            )
          );
        })}
      </div>

      <ClockDetail
        showModal={showModal}
        clockDetails={clockDetails}
        handleCloseModal={(event) => {
          event.preventDefault();
          setShowModal(false);
        }}
        isEditable={false}
      />
    </React.Fragment>
  );
};

export default TimeClockHistory;
